import { useEffect, useRef, useState } from 'react';

import { Button, Icon, IconButton } from '@ds/core';
import { Tooltip } from '@ds/feedback';
import { Textarea } from '@ds/forms';
import { dataCurta, horaDe } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { TIPOS_DE_ARQUIVO_ACEITOS } from '@/data/midiaDaMensagem';
import { dados } from '@/data/porta';
import { camposPorPreencher, preencherMensagemRapida } from '@/data/comunicacao';
import type { SinaisDaConversa } from '@/data/comunicacao';
import type { MensagemRapida } from '@/data/cadastros';
import type { Conversa } from '@/data/tipos';

const BARRA_NO_FIM = /(?:^|\n)\/([\w-]*)$/;

export interface CompositorDaConversaProps {
    conversa: Conversa;
    canalConectado: boolean;
    /** Provedor com que a VecchIA ainda não fala: o texto que diz por onde responder. */
    canalSemIntegracao: string | null;
    sinais?: SinaisDaConversa;
    mensagensRapidas: MensagemRapida[];
    onEnviada: () => void;
}

/**
 * O campo de resposta, com as mensagens rápidas e o anexo.
 *
 * "/" no começo de uma linha abre a lista, e o que vem depois filtra pelo
 * gatilho — o atalho que a equipe já usa no WhatsApp. O botão faz o mesmo para
 * quem não conhece o atalho. Campo da mensagem que não tem valor na conversa
 * fica escrito e trava o envio: "{data}" chegando ao paciente seria erro à vista.
 *
 * Enter envia e Shift+Enter quebra a linha, como no WhatsApp Web. Com a lista de
 * mensagens rápidas aberta, Enter usa a primeira sugestão. Durante a composição
 * de acento (IME), Enter é do teclado, não do envio.
 */
export function CompositorDaConversa({ conversa, canalConectado, canalSemIntegracao, sinais, mensagensRapidas, onEnviada }: CompositorDaConversaProps) {
    const aviso = useAviso();
    const arquivoRef = useRef<HTMLInputElement>(null);
    const [texto, setTexto] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [rapidas, setRapidas] = useState<{ aberto: boolean; filtro: string; pelaBarra: boolean }>({ aberto: false, filtro: '', pelaBarra: false });

    /* Trocar de conversa leva o rascunho embora: responder a Marina com o texto
       que era para a Clara é o erro que ninguém percebe antes de enviar. */
    useEffect(() => {
        setTexto('');
        setRapidas({ aberto: false, filtro: '', pelaBarra: false });
    }, [conversa.id]);

    const fechar = () => setRapidas({ aberto: false, filtro: '', pelaBarra: false });

    const aoDigitar = (valor: string) => {
        setTexto(valor);
        const m = BARRA_NO_FIM.exec(valor);
        if (m) setRapidas({ aberto: true, filtro: m[1] ?? '', pelaBarra: true });
        else if (rapidas.pelaBarra) fechar();
    };

    const sugestoes = rapidas.aberto
        ? mensagensRapidas.filter((m) => m.ativa
            && (!rapidas.filtro || `${m.gatilho} ${m.titulo}`.toLowerCase().includes(rapidas.filtro.toLowerCase())))
        : [];

    const usar = (m: MensagemRapida) => {
        const proximo = sinais?.proximoAgendamento;
        const { texto: preenchido } = preencherMensagemRapida(m.texto, {
            paciente: conversa.nome,
            ...(proximo ? { data: dataCurta(proximo.inicioEm), hora: horaDe(proximo.inicioEm), profissional: proximo.profissional } : {}),
        });
        setTexto((atual) => {
            if (BARRA_NO_FIM.test(atual)) return atual.replace(BARRA_NO_FIM, (casa) => (casa.startsWith('\n') ? '\n' : '') + preenchido);
            if (!atual) return preenchido;
            return `${atual}${atual.endsWith('\n') ? '' : '\n'}${preenchido}`;
        });
        fechar();
        dados().usarMensagemRapida(m.id).catch((e: unknown) => {
            aviso({ title: 'O uso da mensagem rápida não foi contado', description: e instanceof Error ? e.message : undefined, tone: 'warning' });
        });
    };

    const faltando = camposPorPreencher(texto);

    const enviar = async () => {
        if (enviando || !texto.trim() || faltando.length || canalSemIntegracao) return;
        setEnviando(true);
        try {
            await dados().enviarMensagem(conversa.id, texto.trim());
            setTexto('');
            onEnviada();
        } catch (e) {
            aviso({ title: 'Não foi possível enviar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
            /* A mensagem foi gravada antes da tentativa de entrega, e ficou
               marcada como não enviada. Recarregar é o que a põe na thread —
               esconder até o próximo refresh faria parecer que ela sumiu. */
            setTexto('');
            onEnviada();
        } finally {
            setEnviando(false);
        }
    };

    /* Imagem ou PDF só com o WhatsApp conectado: arquivo não fica "pendente"
       esperando um canal que talvez não volte. O texto digitado vai junto,
       como legenda — e por isso campo por preencher também trava o anexo. */
    const anexar = (arquivo: File | undefined) => {
        if (!arquivo) return;
        setEnviando(true);
        dados().enviarArquivo(conversa.id, arquivo, texto)
            .then(() => { setTexto(''); onEnviada(); })
            .catch((erro: unknown) => aviso({
                title: 'Não foi possível enviar o arquivo',
                description: erro instanceof Error ? erro.message : undefined,
                tone: 'danger',
            }))
            .finally(() => setEnviando(false));
    };

    return (
        <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {canalSemIntegracao ? (
                /* Provedor com que a VecchIA ainda não fala: a resposta não
                   "sai depois" — não sairia nunca. Diz por onde responder. */
                <p style={{ margin: 0, fontSize: 12, color: 'var(--warning-strong)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="triangle-alert" size={13} />
                    {canalSemIntegracao}
                </p>
            ) : !canalConectado && (
                <p style={{ margin: 0, fontSize: 12, color: 'var(--warning-strong)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="triangle-alert" size={13} />
                    {conversa.canalNome} está desconectado: a resposta fica gravada e sai quando o canal conectar.
                </p>
            )}
            {faltando.length > 0 && (
                <p role="status" style={{ margin: 0, fontSize: 12, color: 'var(--danger-strong)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="circle-alert" size={13} />
                    Preencha {faltando.join(', ')} antes de enviar: esta conversa não tem agendamento para completar sozinha.
                </p>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', position: 'relative' }}>
                {rapidas.aberto && (
                    <div
                        role="listbox" aria-label="Mensagens rápidas"
                        style={{
                            position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, zIndex: 41, width: 'min(460px, 100%)', maxHeight: 260, overflowY: 'auto',
                            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: 6,
                        }}
                    >
                        {sugestoes.length === 0 ? (
                            <p style={{ margin: 0, padding: '18px 12px', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>
                                Nenhuma mensagem rápida com esse gatilho.
                            </p>
                        ) : sugestoes.map((m) => (
                            <button
                                key={m.id} type="button" role="option" aria-selected={false} onClick={() => usar(m)}
                                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', padding: '9px 10px', border: 0, background: 'transparent', borderRadius: 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left' }}
                            >
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-brand)', flex: '0 0 auto', marginTop: 1 }}>/{m.gatilho}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{m.titulo}</span>
                                    <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.texto}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                <input
                    ref={arquivoRef} type="file" accept={TIPOS_DE_ARQUIVO_ACEITOS.join(',')} style={{ display: 'none' }}
                    aria-hidden="true" tabIndex={-1}
                    onChange={(e) => {
                        const arquivo = e.target.files?.[0];
                        e.target.value = '';
                        anexar(arquivo);
                    }}
                />
                <Tooltip label="Mensagens rápidas (ou digite /)">
                    <IconButton
                        icon={<Icon name="zap" size={16} />} label="Mensagens rápidas" variant="outline" active={rapidas.aberto}
                        disabled={Boolean(canalSemIntegracao)}
                        onClick={() => setRapidas((r) => (r.aberto ? { aberto: false, filtro: '', pelaBarra: false } : { aberto: true, filtro: '', pelaBarra: false }))}
                    />
                </Tooltip>
                <IconButton
                    icon={<Icon name="paperclip" size={15} />} label="Anexar imagem ou PDF" variant="outline"
                    disabled={Boolean(canalSemIntegracao) || !canalConectado || enviando || faltando.length > 0}
                    onClick={() => arquivoRef.current?.click()}
                />
                <Textarea
                    value={texto} onChange={aoDigitar} rows={2} placeholder="Escreva a resposta… / para mensagem rápida · Shift+Enter quebra a linha"
                    aria-label={`Responder a ${conversa.nome}`}
                    disabled={Boolean(canalSemIntegracao)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && rapidas.aberto) { fechar(); return; }
                        if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                        e.preventDefault();
                        const primeira = sugestoes[0];
                        if (rapidas.aberto && primeira) usar(primeira);
                        else void enviar();
                    }}
                    style={{ flex: 1 }}
                />
                <Button onClick={() => void enviar()} loading={enviando} disabled={!texto.trim() || faltando.length > 0 || Boolean(canalSemIntegracao)}>Enviar</Button>
            </div>
        </div>
    );
}
